import styled from 'styled-components'

import { H4 } from '@/@bothrs-ui/web/components/typography/Heading'

import type { ComponentPropsWithoutRef } from 'react'

/**
 * Lists of items that belong to something else, grouped under a heading each: the tasks, messages
 * and documents of an agenda item. The list knows the shape only; what a row shows and what
 * opening it does is composed by the call site, so nothing here knows a task from a message.
 */

/* ------------------------------------------------------------------------------------------------- *
 * RelatedList
 * ------------------------------------------------------------------------------------------------- */

type RelatedListRootProps = ComponentPropsWithoutRef<'div'>

const RelatedListRoot = ({ children, ...props }: RelatedListRootProps) => <StRoot {...props}>{children}</StRoot>

/* ------------------------------------------------------------------------------------------------- *
 * RelatedList.Group
 * ------------------------------------------------------------------------------------------------- */

// A heading and its rows. The rows carry the spacing to the heading through their own padding, and
// the group is where the last row loses its divider.
type RelatedListGroupProps = ComponentPropsWithoutRef<'div'>

const RelatedListGroup = ({ children, ...props }: RelatedListGroupProps) => <StGroup {...props}>{children}</StGroup>

/* ------------------------------------------------------------------------------------------------- *
 * RelatedList.Title
 * ------------------------------------------------------------------------------------------------- */

// An h3 for the outline, in the heading size the design gives it.
type RelatedListTitleProps = ComponentPropsWithoutRef<typeof H4>

const RelatedListTitle = ({ children, ...props }: RelatedListTitleProps) => (
  <H4 as='h3' noMargin {...props}>
    {children}
  </H4>
)

/* ------------------------------------------------------------------------------------------------- *
 * RelatedList.Row
 * ------------------------------------------------------------------------------------------------- */

// Each row is a button, so it opens from the keyboard too.
type RelatedListRowProps = ComponentPropsWithoutRef<'button'>

const RelatedListRow = ({ children, ...props }: RelatedListRowProps) => (
  <StRow type='button' {...props}>
    {children}
  </StRow>
)

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const RelatedList = Object.assign(RelatedListRoot, {
  Group: RelatedListGroup,
  Title: RelatedListTitle,
  Row: RelatedListRow,
})

export { RelatedList }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const StRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space6};
`

const StGroup = styled.div`
  display: flex;
  flex-direction: column;
`

// A row is a button: its own look is reset, and a keyboard focus draws a ring in place of the outline.
const StRow = styled.button`
  display: block;
  width: 100%;
  margin: 0;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4} 0;
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.Colors.Neutrals.Light};
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.Components.Button.Primary.Fill};
  }
`
